<script setup lang="ts">
import { ref, onMounted } from 'vue'

export type CalendarType = 'google' | 'outlook' | 'apple'

const STORAGE_KEY = 'nitte-text-receive-calendar-type'

const show = ref(false)
const selected = ref<CalendarType | null>(null)
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

const emit = defineEmits<{
  (e: 'select', type: CalendarType): void
}>()

onMounted(() => {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'google' || saved === 'outlook' || (saved === 'apple' && isMobile)) {
    selected.value = saved
  } else {
    selected.value = 'google'
  }
})

const open = () => {
  show.value = true
}

const select = (type: CalendarType) => {
  selected.value = type
  localStorage.setItem(STORAGE_KEY, type)
  show.value = false
  emit('select', type)
}

const getSelected = (): CalendarType | null => selected.value

defineExpose({ open, getSelected })
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="show = false">
    <div class="modal-card">
      <div class="modal-header">
        <span class="modal-title">使用するカレンダー</span>
        <button class="modal-close" @click="show = false">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <button
          class="cal-option"
          :class="{ active: selected === 'google' }"
          @click="select('google')"
        >
          <span class="cal-option-name">Google Calendar</span>
          <span v-if="selected === 'google'" class="cal-check">✓</span>
        </button>
        <button
          class="cal-option"
          :class="{ active: selected === 'outlook' }"
          @click="select('outlook')"
        >
          <span class="cal-option-name">Outlook</span>
          <span v-if="selected === 'outlook'" class="cal-check">✓</span>
        </button>
        <button
          v-if="isMobile"
          class="cal-option"
          :class="{ active: selected === 'apple' }"
          @click="select('apple')"
        >
          <span class="cal-option-name">Apple Calendar</span>
          <span v-if="selected === 'apple'" class="cal-check">✓</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}
.modal-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 260px;
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
}
.modal-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--font-black);
}
.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: var(--font-gray-3);
  cursor: pointer;
  border-radius: 4px;
}
.modal-close:hover {
  background: #f3f4f6;
}
.modal-body {
  padding: 0 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cal-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: none;
  font-size: 0.8125rem;
  color: var(--font-black);
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.12s ease;
}
.cal-option:hover {
  background: #f3f4f6;
}
.cal-option.active {
  background: var(--bg-main);
}
.cal-option-name {
  flex: 1;
  text-align: left;
}
.cal-check {
  color: var(--main);
  font-weight: 600;
  font-size: 0.875rem;
}
</style>
